import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { getProductImage } from '@/lib/productMedia';
import { isDigitalProduct } from '@/lib/productType';
import { useCart } from '@/hooks/useCart';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { normalizeCurrency } from '@/lib/currency';
import { useHeroBanners } from '@/hooks/useHeroBanners';
import { heroBannerToItem, isBannerVisible, buildDefaultHeroBanner } from '@/lib/heroBanners';
import HeroBannerSlider from '@/components/feature/HeroBannerSlider';
import { htmlToText, resolveStoreName } from '@/lib/display';
import { useHomeConfig } from '@/hooks/useHomeConfig';
import { normalizeBlockConfig, restaurantBlockOrder } from '@/lib/homeBlocks';
import { getOpenState } from '@/lib/restaurantHours';
import { MENU_ROLES, normalizeMenuRole } from '@/lib/menuRole';
import { ProductsGrid, type HomeProduct } from './ProductGrid';
import { AboutSection } from './HomeSections';
import ThemeExtraBlocksRenderer from '@/components/feature/ThemeExtraBlocksRenderer';
import HomeEventsSection from './HomeEventsSection';

// ─────────────────────────────────────────────────────────────
// Page d'accueil publique — type de compte « Restauration » (cas 4)
// Bannière → Promo du moment → Notre menu (grille 3×3) → À propos.
// Affiche une grande bannière « Restaurant fermé » hors des horaires
// d'ouverture définis dans le dashboard.
// ─────────────────────────────────────────────────────────────

const MENU_LIMIT = 9;
const MENU_FETCH_LIMIT = 60;

export default function RestaurantHome() {
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const { banners } = useHeroBanners(merchantId);
  const { config, loading: configLoading } = useHomeConfig(merchantId);
  const { addToCart } = useCart();
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const { display, original } = useDisplayCurrency(monaie);

  const [promo, setPromo] = useState<HomeProduct | null>(null);
  const [menu, setMenu] = useState<HomeProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [menuRes, promoRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('*')
            .eq('idcommerce', merchantId)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(MENU_FETCH_LIMIT),
          config.promoProductId
            ? supabase.from('product_items').select('*').eq('id', config.promoProductId).maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;
        setMenu((menuRes.data as HomeProduct[]) || []);
        setPromo((promoRes.data as HomeProduct) || null);
      } catch {
        if (!cancelled) {
          setMenu([]);
          setPromo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId, config.promoProductId]);

  const storeName = resolveStoreName(tenant, theme);
  const heroItems = banners.filter(isBannerVisible).map(heroBannerToItem);
  const defaultHero = heroItems.length === 0
    ? buildDefaultHeroBanner({ image: tenant?.imageaboutus, title: storeName, description: tenant?.description || tenant?.aboutus })
    : null;
  const heroDisplay = defaultHero ? [defaultHero] : heroItems;
  const storeDescription = htmlToText(tenant?.description || tenant?.aboutus);
  const aboutTitle = config.aboutTitle.trim() || 'À propos';
  const aboutText = config.aboutContent.trim() || storeDescription;
  const openState = getOpenState(config.openingHours);

  // Regroupe les plats par section de carte (entrées, plats, desserts, boissons).
  const menuGroups = useMemo(() => {
    const byRole = new Map<string, HomeProduct[]>();
    const unassigned: HomeProduct[] = [];
    menu.forEach((p) => {
      const role = normalizeMenuRole(p.menu_role);
      if (role) {
        const arr = byRole.get(role) || [];
        arr.push(p);
        byRole.set(role, arr);
      } else {
        unassigned.push(p);
      }
    });
    const groups = MENU_ROLES
      .map((def) => ({ def, items: byRole.get(def.key) || [] }))
      .filter((g) => g.items.length > 0);
    return { groups, unassigned };
  }, [menu]);
  const hasMenuRoles = menuGroups.groups.length > 0;

  const handleAdd = (product: HomeProduct) => {
    const price = Number(product.price || 0);
    const isPromo = product.discount_enabled && product.discount_price != null;
    const effective = isPromo ? Number(product.discount_price) : price;
    const digital = isDigitalProduct(product);
    addToCart({
      id: product.id,
      name: product.name,
      price: effective,
      original_price: isPromo ? price : null,
      media: getProductImage(product.media) || '',
      stock: digital ? 9999 : (product.stock ?? 9999),
      product_type: product.product_type || 'physical',
      digital_file_url: product.digital_file_url,
    });
  };

  if (loading || configLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  const blocks = normalizeBlockConfig(config.blocks);
  const order = restaurantBlockOrder(blocks);

  const closedBanner = !openState.open ? (
    <section key="closed" className="bg-primary-500 text-background-50">
      <div className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center justify-center gap-3 text-center">
        <i className="ri-store-2-line text-2xl md:text-3xl"></i>
        <div>
          <p className="text-base md:text-xl font-bold font-heading leading-tight">
            Le restaurant est actuellement fermé
          </p>
          <p className="text-xs md:text-sm opacity-90 mt-0.5">
            {openState.todayLabel && openState.todayLabel !== 'Fermé'
              ? `Nous vous accueillons aujourd'hui de ${openState.todayLabel}.`
              : "Merci de votre visite, revenez à nos heures d'ouverture."}
          </p>
        </div>
      </div>
    </section>
  ) : null;

  const bannerSection = heroDisplay.length > 0 ? (
    <HeroBannerSlider key="banner" banners={heroDisplay} />
  ) : (
    <section key="banner" className="relative py-14 md:py-20">
      <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
      <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
        <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-3">{storeName}</h1>
        {storeDescription && (
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">{storeDescription}</p>
        )}
      </div>
    </section>
  );

  const promoSection = promo ? (
    <section key="promo" className="py-8 md:py-14">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center">
            <i className="ri-fire-line text-lg"></i>
          </span>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Promo du moment</h2>
        </div>
        <div
          data-product-shop="true"
          className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2"
        >
          <Link to={`/product/${promo.id}`} className="block h-56 md:h-full min-h-[224px] bg-background-100 overflow-hidden no-underline">
            <img
              src={getProductImage(promo.media) || ''}
              alt={promo.name}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </Link>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <span className="self-start px-3 py-1 mb-3 bg-accent-500 text-background-50 rounded-full text-xs font-semibold">
              Offre du moment
            </span>
            <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">{promo.name}</h3>
            {promo.description && (
              <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-3">{promo.description}</p>
            )}
            <div className="flex items-baseline gap-3 mb-5">
              {promo.discount_enabled && promo.discount_price != null ? (
                <>
                  <span className="text-2xl font-bold text-primary-600">
                    {display(Number(promo.discount_price), normalizeCurrency(promo.currency || monaie))}
                  </span>
                  <span className="text-sm text-foreground-400 line-through">
                    {display(Number(promo.price || 0), normalizeCurrency(promo.currency || monaie))}
                  </span>
                </>
              ) : (
                <span className="text-2xl font-bold text-primary-600">
                  {display(Number(promo.price || 0), normalizeCurrency(promo.currency || monaie))}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => handleAdd(promo)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-cart-line"></i>
                Ajouter au panier
              </button>
              <Link
                to={`/product/${promo.id}`}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap"
              >
                Voir le plat
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  ) : null;

  const menuSection = menu.length > 0 ? (
    <section key="products" className="py-8 md:py-14 bg-background-100/50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center">
            <i className="ri-restaurant-2-line mr-2 text-primary-500"></i>
            Notre menu
          </h2>
          <Link to="/products" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer no-underline whitespace-nowrap">
            Voir plus <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        {hasMenuRoles ? (
          <div className="space-y-10">
            {menuGroups.groups.map((g) => (
              <div key={g.def.key}>
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="w-9 h-9 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
                    <i className={`${g.def.icon} text-lg`}></i>
                  </span>
                  <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-950">{g.def.sectionLabel}</h3>
                  <span className="text-xs font-medium text-foreground-500 bg-background-200/50 px-2 py-0.5 rounded-full">{g.items.length}</span>
                </div>
                <ProductsGrid products={g.items} monaie={monaie} columns={3} />
              </div>
            ))}
            {menuGroups.unassigned.length > 0 && (
              <div>
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="w-9 h-9 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-restaurant-line text-lg"></i>
                  </span>
                  <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-950">Autres</h3>
                </div>
                <ProductsGrid products={menuGroups.unassigned} monaie={monaie} columns={3} />
              </div>
            )}
          </div>
        ) : (
          <ProductsGrid products={menu.slice(0, MENU_LIMIT)} monaie={monaie} columns={3} />
        )}
        <div className="mt-8 flex justify-center">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            Voir plus de plats
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  ) : null;

  const aboutSection = aboutText ? (
    <AboutSection key="about" title={aboutTitle} content={aboutText} />
  ) : null;

  const sectionByKey: Record<string, ReactNode> = {
    banner: bannerSection,
    promo: promoSection,
    products: menuSection,
    about: aboutSection,
  };

  const rendered = order.map((key) => sectionByKey[key]).filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50">
      {closedBanner}
      {rendered.length === 0 ? (
        <section className="py-20">
          <div className="flex flex-col items-center justify-center text-center px-4">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-restaurant-2-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">Notre menu arrive bientôt</p>
          </div>
        </section>
      ) : (
        rendered
      )}
      <HomeEventsSection />
      <ThemeExtraBlocksRenderer />
    </div>
  );
}